/** One line about what just happened, in the hue of how it went.
 *
 * The admin's data screens act on rows — an upload moved, an account
 * disabled, a stream ended — and the row itself shows the new state. What it
 * cannot show is the part of the reply that is not a state: that the bucket
 * refused a delete, that the stream object had nothing open to close, that
 * the account was already disabled and nothing was written. That sentence
 * goes here, as a status rather than an alert, because the action succeeded
 * and only its footnote needs saying.
 */

import type { ReactNode } from 'react';

export type NoticeTone = 'ok' | 'warn' | 'error' | 'neutral';

export interface NoticeProps {
  tone?: NoticeTone;
  children: ReactNode;
  onDismiss?: () => void;
}

const COLOUR: Record<NoticeTone, string> = {
  ok: 'var(--color-ok)',
  warn: 'var(--color-warn)',
  error: 'var(--color-error)',
  neutral: 'var(--color-neutral-500)',
};

export function Notice({ tone = 'neutral', children, onDismiss }: NoticeProps) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid color-mix(in srgb, ${COLOUR[tone]} 45%, transparent)`,
        fontSize: 12.5,
        color: 'var(--color-neutral-300)',
        textWrap: 'pretty',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          flex: 'none',
          marginTop: 6,
          background: COLOUR[tone],
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {onDismiss ? (
        <button
          type="button"
          className="btn btn-ghost"
          aria-label="dismiss"
          style={{ fontSize: 11, padding: '0 4px', color: 'var(--color-neutral-500)' }}
          onClick={onDismiss}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

export default Notice;
